"use client";

import React, { useMemo } from "react";
import { useTheme } from "styled-components";
import styled from "styled-components";
import {
  Asset,
  Item,
  AssetType,
  ImageView,
  ActivityIndicator,
  Icon,
  Flex,
} from "@thenounproject/lingo-core";

import { getAssetError } from "@helpers/getAssetError";
import { getAssetIcon } from "@helpers/getAssetIcon";
import getAssetBackground from "@helpers/getAssetBackground";
import useResponsiveThumbnailSize from "@hooks/useResponsiveThumbnailSize";

const BadgeContainer = styled(Flex).attrs({
  background: "white",
  height: "16px",
  width: "20px",
  position: "absolute",
  borderRadius: "default",
  justifyContent: "center",
  alignItems: "center",
})`
  z-index: 1;
  top: 0px;
  left: 0px;
  box-shadow: 0px 0px 1px rgba(42, 42, 42, 0.25);
  pointer-events: none;
  overflow: hidden;
`;

const ImageContainer = styled(Flex).attrs({
  position: "relative",
  width: "100%",
  height: "100%",
})`
  & img {
    position: static;
    transform: none;
    max-width: 100%;
    height: auto;
    display: block;
  }
`;

type Props = {
  asset: Asset;
  item?: Item;
  lazy?: boolean;
  onLoad?: (success: boolean) => void;
};

function AssetPreviewImage({ asset, lazy = true, onLoad }: Props) {
  const theme = useTheme();
  const { ref, inView, thumbnailSize } = useResponsiveThumbnailSize();

  const assetError = getAssetError(asset);
  const assetIcon = getAssetIcon(asset);
  const backgroundColor = useMemo(() => getAssetBackground(asset, theme), [asset, theme]);
  const hasPadding = asset.meta.backgroundColor !== "clear";

  const isSketch = AssetType.sketchTypes.has(asset.type);
  const isModel = AssetType.modelTypes.has(asset.type);
  const badge =
    isSketch || isModel ? (
      <BadgeContainer>
        <Icon width={12} iconId={isSketch ? "info.sketch" : "content.model"} />
      </BadgeContainer>
    ) : null;

  const shouldRender = lazy ? inView : true;

  function renderContent() {
    if (asset.meta.assetProcessing === "error" || assetError) {
      return (
        <ImageView width="100%" height="100%" icon="info.error" iconSize={24} iconFill="error" />
      );
    }
    if (!shouldRender || asset.meta.assetProcessing !== "complete") {
      return <ActivityIndicator size="small" color="gray" center />;
    }
    return (
      <ImageView
        width="100%"
        height="100%"
        src={asset.thumbnails?.[thumbnailSize]}
        srcFallback={asset.thumbnailPlaceholders?.[thumbnailSize]}
        icon={assetIcon}
        iconSize={32}
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        onLoad={onLoad}
      />
    );
  }

  return (
    <ImageContainer ref={ref} p={hasPadding ? "8px" : undefined} {...backgroundColor}>
      {badge}
      {renderContent()}
    </ImageContainer>
  );
}

export default React.memo(AssetPreviewImage);
